import { useState } from "react";
import { ResultOf } from "gql.tada";
import { motion, useAnimate, useMotionValue } from "motion/react";
import { contentfulClient } from "../../../utils/contentfulClient";
import { PartnershipListSectionById } from "../../../queries/entries/PartnershipListSectionById";
import { PartnershipListItemFragment } from "../../../queries/fragments/PartnershipListItemFragment";
import PartnershipListItem from "./PartnershipListItem";
import { animationCurves } from "../../../utils/animationCurves";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { Button } from "../../shared/core/Button";

type TPartnershipListItem = ResultOf<typeof PartnershipListItemFragment>

export const PartnershipListSection = ({
  entry: { partnershipListSection: entry }
}: {
  entry: ResultOf<typeof PartnershipListSectionById>
}) => {
  const rowHeight = 280;
  const items = entry?.itemsCollection?.items as unknown as TPartnershipListItem[]
  const [listItems, setListItems] = useState<TPartnershipListItem[]>(items);
  const [isLoading, setIsLoading] = useState(false);
  const [centeredItem, setCenteredItem] = useState<HTMLDivElement | null>(null);
  const [mobileVpObserver] = useState<IntersectionObserver>(() =>
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setCenteredItem(entry.target as HTMLDivElement);
        }
      })
    }, {
      threshold: 1,
    })
  );
  const barY = useMotionValue(-rowHeight);
  const [scope, animate] = useAnimate();
  const isDesktop = useIsDesktop();

  if (!entry || !listItems || !listItems.length) {
    return null;
  }
  const entryId = atob(entry._id).replace(/^.+\/([^/]+)$/, "$1");

  const handleMouseOver = (idx: number) => {
    animate(barY, rowHeight * idx, animationCurves.macro);
  }

  const handleMouseOut = () => {
    animate(barY, -rowHeight, animationCurves.macro);
  }

  const handleViewMoreClick = async () => {
    setIsLoading(true);
    const queryResult = await contentfulClient.query(PartnershipListSectionById, {
      id: entryId,
      skip: listItems.length,
    });
    const fetchedItems = queryResult?.data?.partnershipListSection?.itemsCollection?.items;
    setListItems([
      ...listItems,
      ...fetchedItems as unknown as TPartnershipListItem[],
    ]);
    setIsLoading(false);
  }

  return (
    <section className="min-h-screen flex flex-col items-center pb-32 pt-16">
      <div
        className="overflow-hidden w-full relative flex flex-col gap-10 dsk:gap-0"
        onMouseLeave={handleMouseOut}
      >
        {/* Highlight bar */}
        <motion.div
          ref={scope}
          className="absolute w-full bg-palette-current-deep pointer-events-none"
          style={{
            height: isDesktop ? rowHeight : "auto",
            y: barY,
          }}
        />
        {
          listItems.map((item, idx) => {
            if (item) {
              return (
                <PartnershipListItem
                  key={idx}
                  item={item as unknown as TPartnershipListItem}
                  index={idx}
                  height={rowHeight}
                  centeredItem={centeredItem}
                  mobileVpObserver={mobileVpObserver}
                  onMouseOver={handleMouseOver}
                />
              )
            }
          })
        }
      </div>
      {
        listItems.length < (entry.itemsCollection?.total || 0) && (
          <div className="mt-20">
            <Button onClick={handleViewMoreClick} loading={isLoading}>
              <span>{entry.viewMoreText}</span>
            </Button>
          </div>
        )
      }
    </section>
  );
};
